import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import Swal from 'sweetalert2';
import { useImperativeQuery } from 'hooks/useImperativeQuery.hook';
import { useHistory, useLocation } from 'react-router-dom';

import { EQueryStringKey } from 'services/QueryString/QueryStringService';
import {
  GET_TITLE_BY_WRITER_ID,
  CHECK_IF_TITLE_HAS_WRITER,
} from '../../../queries/titleWriter';
import ListTitles from './ListTitles';
import HeaderTitles from './HeaderTitles';
import { usePermissions } from '../../../store/permissions.context';
import { useUserData } from '../../../hooks/context/userUserData.hook';
import Layout from '../../../components/Layout/Layout';

export default function QueryTitlesWriter() {
  const location = useLocation();
  const history = useHistory();

  const [user] = useUserData();

  const [permissions, setPermissions] = usePermissions();
  const [titles, setTitles] = useState([]);
  const [isByPublishPost, setIsByPublishPost] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(
    moment().add(-6, 'days').startOf('day').toDate(),
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    moment().endOf('day').toDate(),
  );
  const [dateTypeFilter, setDateTypeFilter] = useState('Last7Days');

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(10);
  const [filterSearchText, setFilterSearchText] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [statusCanBeOpened, setStatusCanBeOpened] = useState([]);

  const initializeId = '000000000000';

  const queryTitles = useQuery(GET_TITLE_BY_WRITER_ID, {
    variables: {
      writerId: user.id,
      offset: page * limit,
      limit,
      dateStart: selectedStartDate,
      dateEnd: selectedEndDate,
      searchText: filterSearchText,
      status: filterStatus,
      byPublishPost: isByPublishPost,
    },
    fetchPolicy: 'network-only',
  });

  const callQuery = useImperativeQuery(CHECK_IF_TITLE_HAS_WRITER, {
    variables: { id: initializeId, writerId: user.id },
  });

  const { loading, error, refetch } = queryTitles;
  const refetchTitles = async () => {
    return queryTitles.refetch();
  };

  useEffect(() => {
    if (queryTitles.data) {
      const {
        data: {
          titlesAsWriter: { titles, permissions, total },
        },
      } = queryTitles;
      setTitles(titles);
      setTotal(total);
      setPermissions(permissions);
      setStatusCanBeOpened(permissions.statusCanBeOpened);
    }
  }, [queryTitles, setPermissions]);

  const checkHasWriter = async (
    data,
    setSelectedTitleId,
    setSelectedTitleStatus,
    setShowDrawer,
  ) => {
    const { data: dataHasWriter } = await callQuery({ id: data.titleId });
    if (!dataHasWriter.checkHasWriter.hasWriter) {
      await Swal.fire({
        title: 'Are you sure?',
        text:
          'The status will be changed to "working" and you will be able to write one article at time.',
        icon: 'warning',
        showCancelButton: true,
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, access title!',
      }).then((result) => {
        if (result.value) {
          setSelectedTitleId(data.titleId);
          setSelectedTitleStatus(data.status);
          setShowDrawer(true);

          const params = new URLSearchParams({
            [EQueryStringKey.TitleId]: data.titleId,
          });
          history.replace({
            pathname: location.pathname,
            search: params.toString(),
          });
        }
      });
    } else {
      Swal.fire({
        title: 'Alert!',
        text: 'Another writer is already working on this title',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
    }
  };

  return (
    <Layout>
      <HeaderTitles
        titles={titles}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        isByPublishPost={isByPublishPost}
        setIsByPublishPost={setIsByPublishPost}
        selectedStartDate={selectedStartDate}
        setSelectedStartDate={setSelectedStartDate}
        selectedEndDate={selectedEndDate}
        setSelectedEndDate={setSelectedEndDate}
        setPage={setPage}
        dateTypeFilter={dateTypeFilter}
        setDateTypeFilter={setDateTypeFilter}
        refetchTitles={refetchTitles}
        permissions={permissions}
      />
      <ListTitles
        titles={titles}
        setTitles={setTitles}
        total={total}
        loading={loading}
        error={error}
        refetch={refetch}
        refetchTitles={refetchTitles}
        setFilterSearchText={setFilterSearchText}
        setLimit={setLimit}
        setPage={setPage}
        limit={limit}
        page={page}
        checkHasWriter={checkHasWriter}
        fieldsNotShow={['writerId']}
        statusCanBeOpened={statusCanBeOpened}
      />
    </Layout>
  );
}
